import { useEffect } from 'react'

interface SocialMeta {
  ogImage?: string
  ogType?: string
  ogUrl?: string
  twitterCard?: 'summary' | 'summary_large_image'
  /**
   * Absolute or path-only URL that should be treated as the single indexable
   * address for this view. Defaults to the current path with the query string
   * stripped, which collapses tracking and prefill parameters onto one URL.
   */
  canonical?: string
  /**
   * Funnel steps, results, and confirmation screens are dead ends for search
   * traffic — they carry no standalone value and would otherwise be indexed as
   * thin pages.
   */
  noindex?: boolean
}

const SITE_NAME = 'findonlinecollege.com'
const DEFAULT_TITLE = 'findonlinecollege.com | Find Your Path Forward'
const DEFAULT_DESCRIPTION =
  'Compare accredited online colleges and universities. Get matched with degree programs that fit your career goals, schedule, and budget.'
const DEFAULT_OG_IMAGE =
  'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1200&h=630&fit=crop&q=80'

function setMetaTag(
  property: string,
  content: string,
  attrName: 'property' | 'name' = 'property',
) {
  let tag = document.querySelector(
    `meta[${attrName}="${property}"]`,
  ) as HTMLMetaElement | null
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute(attrName, property)
    document.head.appendChild(tag)
  }
  tag.content = content
}

function removeMetaTag(
  property: string,
  attrName: 'property' | 'name' = 'property',
) {
  const tag = document.querySelector(`meta[${attrName}="${property}"]`)
  if (tag) tag.remove()
}

function setCanonical(href: string) {
  let link = document.querySelector(
    'link[rel="canonical"]',
  ) as HTMLLinkElement | null
  if (!link) {
    link = document.createElement('link')
    link.rel = 'canonical'
    document.head.appendChild(link)
  }
  link.href = href
}

function removeCanonical() {
  const link = document.querySelector('link[rel="canonical"]')
  if (link) link.remove()
}

/** Resolves a path-only canonical against the current origin. */
function absoluteUrl(pathOrUrl: string): string {
  try {
    return new URL(pathOrUrl, window.location.origin).href
  } catch {
    return window.location.origin + pathOrUrl
  }
}

export function usePageMeta(
  title: string,
  description: string,
  social?: SocialMeta,
) {
  useEffect(() => {
    const ogImage = social?.ogImage || DEFAULT_OG_IMAGE
    const ogType = social?.ogType || 'website'
    const twitterCard = social?.twitterCard || 'summary_large_image'
    // Query strings carry prefill and tracking values that never change the
    // page's content, so they are dropped from the canonical by default.
    const canonicalHref = absoluteUrl(
      social?.canonical || window.location.pathname,
    )
    const ogUrl = social?.ogUrl || canonicalHref

    document.title = title

    let metaDesc = document.querySelector(
      'meta[name="description"]',
    ) as HTMLMetaElement | null
    if (!metaDesc) {
      metaDesc = document.createElement('meta')
      metaDesc.name = 'description'
      document.head.appendChild(metaDesc)
    }
    metaDesc.content = description

    setCanonical(canonicalHref)

    if (social?.noindex) {
      setMetaTag('robots', 'noindex, follow', 'name')
    } else {
      removeMetaTag('robots', 'name')
    }

    // Open Graph tags
    setMetaTag('og:title', title)
    setMetaTag('og:description', description)
    setMetaTag('og:image', ogImage)
    setMetaTag('og:type', ogType)
    setMetaTag('og:url', ogUrl)
    setMetaTag('og:site_name', SITE_NAME)

    // Twitter Card tags
    setMetaTag('twitter:card', twitterCard, 'name')
    setMetaTag('twitter:title', title, 'name')
    setMetaTag('twitter:description', description, 'name')
    setMetaTag('twitter:image', ogImage, 'name')

    // Cleanup: restore defaults on unmount
    return () => {
      document.title = DEFAULT_TITLE
      if (metaDesc) {
        metaDesc.content = DEFAULT_DESCRIPTION
      }
      removeCanonical()
      removeMetaTag('robots', 'name')
      removeMetaTag('og:title')
      removeMetaTag('og:description')
      removeMetaTag('og:image')
      removeMetaTag('og:type')
      removeMetaTag('og:url')
      removeMetaTag('og:site_name')
      removeMetaTag('twitter:card', 'name')
      removeMetaTag('twitter:title', 'name')
      removeMetaTag('twitter:description', 'name')
      removeMetaTag('twitter:image', 'name')
    }
  }, [
    title,
    description,
    social?.ogImage,
    social?.ogType,
    social?.ogUrl,
    social?.twitterCard,
    social?.canonical,
    social?.noindex,
  ])
}
